import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import MuiButton from "@components/Button/MuiButton";
import {
  Box,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";

import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";
import { toast } from "react-toastify";
import { SubmitWithdrawApplicationThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { fontSize } from "@mui/system";
import ConfirmDialog from "@components/Modal/ConfirmModal";

// import your withdraw thunk
// import { withdrawApplicationThunk } from "../../redux/thunk/caApplicationthunk";

type Props = {
  open: boolean;
  onClose: () => void;
  access_id: string | number;
  onSuccess: () => void;
};

const WithdrawApplicationModel: React.FC<Props> = ({
  open,
  onClose,
  access_id,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [remarks, setRemarks] = useState("");
  const [consent, setConsent] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleCloseSlider = () => {
    setRemarks("");
    setConsent(false);
    onClose();
  };

  const handleWithdrawApplication = async () => {
    try {
      const payload = {
        remarks,
        consent,
      };

      // Replace with actual API
      const res = await dispatch(
        SubmitWithdrawApplicationThunk({
          application_id: access_id,
          payload,
        }),
      );

      if (res?.payload?.success) {
        toast.success(res?.payload?.message);

        setRemarks("");
        setConsent(false);
        setOpenConfirm(false);

        onSuccess?.();

        handleCloseSlider();
      } else {
        setOpenConfirm(false);
        toast.error(
          res?.payload?.errors[0] || "Failed to withdraw application",
        );
      }
    } catch (error) {
      setOpenConfirm(false);
      toast.error("Something went wrong");
    }
  };

  const handleConfirmWithdraw = () => {
    setOpenConfirm(true);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseSlider}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "500px" },
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          {/* Header */}
          <Box sx={{ px: 4, py: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "18px",
                  color: "#212121",
                  lineHeight: "24px",
                }}
              >
                Withdraw Application
              </Typography>

              <CloseIcon
                onClick={handleCloseSlider}
                sx={{
                  cursor: "pointer",
                  "&:hover": { color: "primary.main" },
                }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Body */}
          <form
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ px: 4, py: 3, flex: 1 }}>
              {/* Remarks */}
              <TextField
                label="Please Enter Remarks"
                placeholder="Enter remarks for withdrawal"
                multiline
                rows={15}
                fullWidth
                value={remarks}
                onChange={(e) => {
                  if (e.target.value.length <= 2000) {
                    setRemarks(e.target.value);
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#3a1c73",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#3a1c73",
                  },
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "right",
                  mt: 0.5,
                }}
              >
                {remarks.length}/2000
              </Typography>
            </Box>

            {/* Footer */}
            <Box mt={1} sx={{ bgcolor: "#F4F2F7", px: 2, py: 1 }}>
              <FormControlLabel
                sx={{
                  alignItems: "flex-start",
                  margin: 0,
                  ".MuiFormControlLabel-label": {
                    mt: "2px",
                  },
                }}
                control={
                  <Checkbox
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    sx={{
                      "&.Mui-checked": { color: "#3A1C73" },
                      pt: 0,
                    }}
                  />
                }
                label={
                  <Typography fontSize={12}>
                    I have read and understood the above advisory. I confirm
                    that I wish to withdraw this application voluntarily. I
                    understand that this action is irreversible and that any
                    applicable fee already paid is non-refundable as per
                    applicable rules.
                  </Typography>
                }
              />
            </Box>
            <Box
              px={4}
              py={3}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <MuiButton
                type="button"
                disabled={!remarks.trim() || !consent}
                name="Withdraw Application"
                onClick={handleConfirmWithdraw}
                sx={{
                  width: "100%",
                  borderRadius: "999px",
                  backgroundColor: "#928F25",
                  color: "#fff",
                  py: 1.5,
                  fontSize: 14,

                  "&:hover": {
                    backgroundColor: "#928F25",
                  },

                  "&.Mui-disabled": {
                    backgroundColor: "#757575",
                    color: "#FFFFFF",
                  },
                }}
              />
            </Box>
          </form>
        </Box>
      </Drawer>
      <ConfirmDialog
        open={openConfirm}
        message={
          <>
            <Typography mb={2} fontSize={14} fontWeight="bold">
              Are you sure you want to withdraw this application?
            </Typography>

            <Typography
              fontWeight="bold"
              mb={1}
              sx={{ textDecoration: "underline" }}
            >
              Important Advisory:
            </Typography>
            <Typography mb={1}>
              <ol style={{ paddingLeft: "20px", margin: 0 }}>
                <li>
                  If your application contains incorrect auto-fetched details
                  (such as Aadhaar, PAN, GST, organisation details, etc.) or has
                  been mapped to an{" "}
                  <strong>
                    {" "}
                    incorrect jurisdiction/authority (Regional Officer Office/
                    Project Implementation Unit Office), you may raise a request
                    through the{" "}
                    <a
                      href="/help-desk"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "underline",
                        color: "#3a1c73",
                      }}
                    >
                      Help & Support{" "}
                    </a>
                    section
                  </strong>{" "}
                  for review and correction, wherever permissible.
                </li>
                <li>
                  Once the withdrawal request is submitted, it cannot be
                  revoked.
                </li>
                <li>
                  The application processing will be permanently closed and no
                  further action will be taken on this application.
                </li>
                <li>
                  Any fee or payment already made for this application is
                  non-refundable, as per applicable Government rules.
                </li>
                <li>
                  If you wish to apply again, a new application must be
                  submitted.
                </li>
              </ol>
            </Typography>
          </>
        }
        title="Confirm Withdrawal"
        confirmText="Yes, Withdraw Application"
        cancelText="Cancel"
        onConfirm={handleWithdrawApplication}
        onClose={() => setOpenConfirm(false)}
      />
    </>
  );
};

export default WithdrawApplicationModel;
